import React, { useState, useEffect } from "react";
import { Save, Ticket, Percent, DollarSign, Calendar, AlignLeft, Hash } from "lucide-react";
import FormHeader from "../Common/FormHeader"; 

const FormVoucher = ({ onBack, onSave, initialData = null }) => {
  // Definisi State Awal untuk Voucher Management
  const [formData, setFormData] = useState({
    code: "",
    type: "Percentage", // Percentage or Fixed Amount
    value: "",
    minPurchase: "",
    expiryDate: "",
    quota: "",
    description: ""
  });

  // Sinkronisasi data saat Edit Mode sesuai pola yang kita gunakan
  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        code: initialData.code || "",
        type: initialData.type || "Percentage",
        value: initialData.value || "",
        minPurchase: initialData.minPurchase || "",
        expiryDate: initialData.expiryDate || "",
        quota: initialData.quota || "",
        description: initialData.description || ""
      });
    } else {
      setFormData({
        code: "", type: "Percentage", value: "",
        minPurchase: "", expiryDate: "", quota: "",
        description: ""
      });
    }
  }, [initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="p-6 space-y-6 bg-white dark:bg-slate-900 rounded-[2rem] shadow-sm border border-stone-100 dark:border-white/5 animate-in fade-in duration-500">
      
      {/* HEADER DENGAN TOMBOL BACK */}
      <FormHeader 
        title={initialData ? "Edit Voucher" : "Create New Voucher"}
        subtitle="Set up discount codes and promotional limits for the event"
        onBack={onBack} 
      />

      <form onSubmit={handleSubmit} className="space-y-8 max-w-5xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          
          <div className="space-y-6">
            {/* Voucher Code */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Voucher Code</label>
              <div className="relative">
                <Ticket className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                <input 
                  type="text" required
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})} 
                  className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-bold outline-none focus:border-blue-500 transition-all text-slate-800 dark:text-white uppercase placeholder:normal-case"
                  placeholder="e.g. SUMMIT2026"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Discount Type */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Type</label>
                <div className="relative">
                  {formData.type === "Percentage" ? (
                    <Percent className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                  ) : (
                    <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                  )}
                  <select 
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none focus:border-blue-500 transition-all text-slate-800 dark:text-white appearance-none"
                  >
                    <option value="Percentage">Percentage</option>
                    <option value="Fixed">Fixed Amount</option>
                  </select>
                </div>
              </div>

              {/* Discount Value */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Value</label>
                <input 
                  type="number" required
                  value={formData.value}
                  onChange={(e) => setFormData({...formData, value: e.target.value})}
                  className="w-full px-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none focus:border-blue-500 transition-all text-slate-800 dark:text-white"
                  placeholder={formData.type === "Percentage" ? "10 (%)" : "50000"}
                />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              {/* Expiry Date */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Expiry Date</label>
                <div className="relative">
                  <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                  <input 
                    type="date" required
                    value={formData.expiryDate}
                    onChange={(e) => setFormData({...formData, expiryDate: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none focus:border-blue-500 transition-all text-slate-800 dark:text-white"
                  />
                </div>
              </div>

              {/* Quota / Limit */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Usage Quota</label>
                <div className="relative">
                  <Hash className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                  <input 
                    type="number" required
                    value={formData.quota}
                    onChange={(e) => setFormData({...formData, quota: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none focus:border-blue-500 transition-all text-slate-800 dark:text-white"
                    placeholder="100"
                  />
                </div>
              </div>
            </div>

            {/* Minimum Purchase */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Min. Purchase (Optional)</label>
              <div className="relative">
                <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                <input 
                  type="number"
                  value={formData.minPurchase}
                  onChange={(e) => setFormData({...formData, minPurchase: e.target.value})}
                  className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none focus:border-blue-500 transition-all text-slate-800 dark:text-white"
                  placeholder="e.g. 200000"
                />
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="md:col-span-2 flex flex-col gap-1.5">
            <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Voucher Terms & Description</label>
            <div className="relative">
              <AlignLeft className="absolute left-4 top-4 text-stone-400" size={14} />
              <textarea 
                rows="3"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full pl-10 pr-4 py-4 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none resize-none leading-relaxed focus:border-blue-500 text-slate-800 dark:text-white"
                placeholder="Describe the terms and conditions for this voucher..."
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="pt-6 border-t border-stone-50 dark:border-white/5 flex justify-end gap-3">
          <button 
            type="button" 
            onClick={onBack}
            className="px-8 py-3 rounded-xl text-sm font-semibold text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-white/5 transition-all"
          >
            Discard
          </button>
          <button 
            type="submit"
            className="px-10 py-3 bg-[#0F172A] dark:bg-blue-600 text-white rounded-xl text-sm font-semibold shadow-lg hover:bg-slate-800 dark:hover:bg-blue-700 transition-all flex items-center gap-2"
          >
            <Save size={18} />
            {initialData ? "Update Voucher" : "Create Voucher"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FormVoucher;